import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Form, Stack } from 'rsuite';
import { AutocompleteWithErrorStatus } from './AutocompleteWithErrorStatus';
import { lookupAddress } from '../../api/apiAccess';
import { useResize } from '../../hooks/useResize';
import { useDebounce } from '../../hooks/useDebounce';
import { titleize } from '../../common/utils';
const DEBOUNCE_DELAY = 500; // 500ms
const MINIMUM_SEARCH_TERM_LENGTH = 3;

const parseSmartyResultToAddress = (result) => {
  return {
    label: `${result.street_line} ${result.secondary} ${result.city} ${result.state} ${result.zipcode}`,
    value: `${result.street_line} ${result.secondary} ${result.city} ${result.state} ${result.zipcode}`,
    components: result, // address components
  };
};

const UnrestrictedAddressLookup = ({
  name,
  label,
  validationRule,
  hasError,
  errorMessage,
  hideErrorIcon = false,
  disabled = false,
  onTextChanged = () => {},
  ...rest
}) => {
  const containerRef = useRef();
  const [menuWidth, setMenuWidth] = useState('0px');
  const [addressOptions, setAddressOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(rest.value);
  const { resizeTrigger } = useResize();

  useEffect(() => {
    // This is required to prevent a ResizeObserver bug
    // It ensures that the autocomplete suggestions menu is can not attempt to
    // expand beyond the size of the input field
    if (containerRef.current) {
      const newWidth = containerRef.current.clientWidth;
      setMenuWidth(`${newWidth}px`);
    }
  }, [resizeTrigger]);

  useEffect(() => {
    setSearchTerm(rest.value);
  }, [rest.value]);

  const realSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);

  const debouncedSearch = useCallback(async (value) => {
    onTextChanged();
    if (value === undefined || value.length < MINIMUM_SEARCH_TERM_LENGTH) {
      setAddressOptions([]);
      return;
    }
    setLoading(true);
    const { data } = await lookupAddress(value);
    const parsedData = data?.suggestions?.map((suggestion) =>
      parseSmartyResultToAddress(suggestion)
    );
    setAddressOptions(parsedData);
    setLoading(false);
  }, []);

  useEffect(() => {
    debouncedSearch(realSearchTerm);
  }, [realSearchTerm, debouncedSearch]);

  const onSearchTermChanged = useCallback((value) => setSearchTerm(value), []);

  const AutocompleteWithErrorStateAndProps = useCallback(
    (props) => (
      <AutocompleteWithErrorStatus
        {...props}
        showError={hasError}
        errorMessage={errorMessage}
        hideErrorIcon={hideErrorIcon}
        isLoading={props.loading === 'true'}
      />
    ),
    [hasError, errorMessage, hideErrorIcon]
  );

  return (
    <Form.Group
      controlId={name}
      {...rest.xs}
      data-testid="unrestricted-address-lookup"
    >
      <Stack direction="row">
        <Form.ControlLabel>{label}</Form.ControlLabel>
      </Stack>
      <Stack ref={containerRef}>
        <Form.Control
          {...rest}
          className={disabled === true ? 'disabled-input' : null}
          style={{ width: menuWidth }}
          name={name}
          rule={validationRule}
          accepter={AutocompleteWithErrorStateAndProps}
          onChange={onSearchTermChanged}
          onSelect={() => {}}
          data={addressOptions}
          disabled={disabled}
          loading={loading.toString()}
          value={titleize(searchTerm)}
        />
      </Stack>
    </Form.Group>
  );
};

export default UnrestrictedAddressLookup;
